/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-22 07:27:37
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { percentRoundFn } from './index';

function componentToHex(c) {
  const hex = c.toString(16);
  return hex.length === 1 ? '0' + hex : hex;
}

export function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : {};
}

/**
 * Convert HSV to RGB
 *
 * @param  {numbr} h hue
 * @param  {numbr} s saturation
 * @param  {numbr} v brightness
 *
 * @return {object}   RGB color
 *                    {
 *                      red: <number>,
 *                      green: <number>,
 *                      blue: <number>
 *                    }
 */
export function hsvToRgb(h, s, v) {
  let r, g, b;
  let i;
  let f, p, q, t;

  // Make sure our arguments stay in-range
  h = Math.max(0, Math.min(360, h));
  s = Math.max(0, Math.min(100, percentRoundFn(s * 100)));
  v = Math.max(0, Math.min(100, percentRoundFn(v * 100)));

  // We accept saturation and value arguments from 0 to 100 because that's
  // how Photoshop represents those values. Internally, however, the
  // saturation and value are calculated from a range of 0 to 1. We make
  // That conversion here.
  s /= 100;
  v /= 100;

  if (s === 0) {
    // Achromatic (grey)
    r = g = b = v;
    return { red: Math.round(r * 255), green: Math.round(g * 255), blue: Math.round(b * 255) };
  }

  h /= 60; // sector 0 to 5
  i = Math.floor(h);
  f = h - i; // factorial part of h
  p = v * (1 - s);
  q = v * (1 - s * f);
  t = v * (1 - s * (1 - f));

  switch (i) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;

    case 1:
      r = q;
      g = v;
      b = p;
      break;

    case 2:
      r = p;
      g = v;
      b = t;
      break;

    case 3:
      r = p;
      g = q;
      b = v;
      break;

    case 4:
      r = t;
      g = p;
      b = v;
      break;

    default:
      // case 5:
      r = v;
      g = p;
      b = q;
  }

  return { red: Math.round(r * 255), green: Math.round(g * 255), blue: Math.round(b * 255) };
}

/**
 * Convert RGB to HEX
 *
 * @param  {number} r red
 * @param  {number} g green
 * @param  {number} b blue
 *
 * @return {string}   hex color
 */
export function rgbToHex(r, g, b) {
  return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
}

/**
 * Convert HSV to HEX
 *
 * @param  {number} h hue
 * @param  {number} s saturation
 * @param  {number} v brightness
 *
 * @return {string}   hex color
 */
export function hsvToHex(h, s, v) {
  const { red, green, blue } = hsvToRgb(h, s, v);
  const hex = rgbToHex(red, green, blue);

  return hex;
}

/**
 * Convert HEX to HSV
 *
 * @param   {string} hex color
 *
 * @return  {object} hsv color
 *                 {
 *                   hue: <number>,
 *                   brightness: number,
 *                   saturation: <number>
 *                 }
 *
 * @return {[type]}     [description]
 */
export function hexToHsv(hex) {
  const rgb = hexToRgb(hex);

  if (!rgb) {
    return { h: 0, s: 0, v: 1 };
  }

  const { r = 255, g = 255, b = 255 } = rgb;

  const hsv = rgbToHsv(r, g, b);

  return hsv;
}

/**
 * Convert RGB to HSV
 *
 * @param  {number} r red
 * @param  {number} g green
 * @param  {number} b blue
 *
 * @return  {object} hsv color
 *                 {
 *                   hue: <number>,
 *                   brightness: number,
 *                   saturation: <number>
 *                 }
 */
export function rgbToHsv(r, g, b) {
  let rabs, gabs, babs, rr, gg, bb, h, s, v, diff, diffc;
  rabs = r / 255;
  gabs = g / 255;
  babs = b / 255;
  v = Math.max(rabs, gabs, babs);
  diff = v - Math.min(rabs, gabs, babs);
  diffc = c => (v - c) / 6 / diff + 1 / 2;
  if (diff === 0) {
    h = s = 0;
  } else {
    s = diff / v;
    rr = diffc(rabs);
    gg = diffc(gabs);
    bb = diffc(babs);

    if (rabs === v) {
      h = bb - gg;
    } else if (gabs === v) {
      h = 1 / 3 + rr - bb;
    } else if (babs === v) {
      h = 2 / 3 + gg - rr;
    }
    if (h < 0) {
      h += 1;
    } else if (h > 1) {
      h -= 1;
    }
  }
  return {
    h: Math.round(h * 360),
    s,
    v
  };
}

/**
 * parseRGBAColor
 * @param  {string} str string color rgba(red, green, blue, opacity)
 * @return {object}     {r, g, b, a}
 */
export function parseRGBAColor(str) {
  const match = str.match(/rgba?\((\d{1,3}), ?(\d{1,3}), ?(\d{1,3}), ?(\d{1,3})\)/);

  if (match) {
    let r = parseInt(match[1]);
    let g = parseInt(match[2]);
    let b = parseInt(match[3]);
    let a = parseInt(match[4]);

    // 0 <= red, green, blue <= 255
    // 0 <= a <= 1
    r = r < 0 ? 0 : r > 255 ? 255 : r;
    g = g < 0 ? 0 : g > 255 ? 255 : g;
    b = b < 0 ? 0 : b > 255 ? 255 : b;
    a = a < 0 ? 0 : a > 1 ? 1 : a;

    return { r, g, b, a };
  }

  return {};
}

/**
 * parseRGBAColorV2
 * @param  {string} str string color from fronend rgba(red||green||blue||opacity)
 * @return {object}     {r, g, b, a}
 */
export function parseRGBAColorFromSpecialFormat(str) {
  // Allow null or empty value
  if (_.isEmpty(str)) {
    return str;
  }

  var match = str.match(/rgba?\((\d{1,3})\|\|?(\d{1,3})\|\|?(\d{1,3})\|\|?(\d{1,3})\)/);
  // var match = str.match(/rgba?((\d+)\|\|\s*(\d+)\|\|\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?)/);
  return match
    ? {
        r: parseInt(match[1]),
        g: parseInt(match[2]),
        b: parseInt(match[3]),
        a: parseInt(match[4])
      }
    : {
        r: 0,
        g: 0,
        b: 0,
        a: 1
      };
}

/**
 * Display rgba color under format rgba(r, g, b, a)
 *
 * @param  {object} rgbaColor
 * @return {string}           string color
 */
export function stringifyRGBA(rgbaColor) {
  return rgbaColor.red
    ? `rgba(${rgbaColor.red}, ${rgbaColor.green}, ${rgbaColor.blue}, ${rgbaColor.alpha})`
    : `rgba(${rgbaColor.r}, ${rgbaColor.g}, ${rgbaColor.b}, ${rgbaColor.a})`;
}

/**
 * Display rgba color under special format rgba(r||g||b||a)
 * @param  {object} rgbaColor
 * @return {string}           string color
 */
export function stringifyRGBASpecialFormat(rgbaColor) {
  return rgbaColor.red
    ? `rgba(${rgbaColor.red}||${rgbaColor.green}||${rgbaColor.blue}||${rgbaColor.alpha})`
    : `rgba(${rgbaColor.r}||${rgbaColor.g}||${rgbaColor.b}||${rgbaColor.a})`;
}

/**
 * Convert hsv color to rgba under format rgba(r||g||b||a)
 * @param  {object} hsvColor
 * @return {string}          string color
 */
export function convertHSVtoRGBASpecialFormat(hsvColor) {
  const { hue, saturation, brightness } = hsvColor;
  let rgbaColor = hsvToRgb(hue, saturation, brightness);
  rgbaColor.alpha = 1;

  return stringifyRGBASpecialFormat(rgbaColor);
}

/**
 * Convert rgba string to object (r||g||b||a)
 * @param  {string} rgbString
 * @return {object}          object rgba color
 */
export function convertRGBAStringToObject(rgbString) {
  if (!rgbString) {
    return {
      a: 1,
      r: 255,
      g: 255,
      b: 255
    };
  }
  let colors = ['r', 'g', 'b', 'a'];
  // Getting the index of "(" and ")"
  // by using the indexOf() method
  let colorArr = rgbString.slice(rgbString.indexOf('(') + 1, rgbString.indexOf(')')).split(', ');
  let obj = {};
  // Insert the values into obj
  colorArr.forEach((k, i) => {
    obj[colors[i]] = Number(k);
  });
  return obj;
}

/**
 * Convert HEX to RGBA
 *
 * @param  {string} hex
 * @return {object}   RGBA string color
 */
export function hexToRgbA(hex) {
  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('');
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = '0x' + c.join('');
    return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',1)';
  }
  return hex;
}

/**
 * Convert RGB to HEX
 *
 * @param  {string|object} rgbParam
 * @return {object}   Hex string color
 */
export function RGBToHex(rgbParam) {
  let rgb = rgbParam;
  if (typeof rgb === 'string') {
    rgb = convertRGBAStringToObject(rgb);
  }
  let { r, g, b } = rgb;
  r = r.toString(16);
  g = g.toString(16);
  b = b.toString(16);

  if (r.length === 1) r = '0' + r;
  if (g.length === 1) g = '0' + g;
  if (b.length === 1) b = '0' + b;

  return '#' + r + g + b;
}

/**
 * Convert RGBA to HEX
 *
 * @param  {string|object} rgbParam
 * @return {object}   Hex string color
 */
export function RGBAToHex(rgbParam) {
  let rgb = rgbParam;
  if (typeof rgb === 'string') {
    rgb = convertRGBAStringToObject(rgb);
  }
  let { r, g, b, a } = rgb;
  r = r.toString(16);
  g = g.toString(16);
  b = b.toString(16);
  if (a !== 1) {
    a = Math.round(a * 255).toString(16);
  }

  if (r.length === 1) r = '0' + r;
  if (g.length === 1) g = '0' + g;
  if (b.length === 1) b = '0' + b;
  if (a.length === 1) a = '0' + a;

  if (a === 1 || a === 0) {
    return '#' + r + g + b;
  }
  return '#' + r + g + b + a;
}

/**
 * Get contrast color
 *
 * @param  {object} color
 * @param {object} options
 * @return {object}  contrast color
 */
export function getContrastColor(
  color,
  options = {
    brightColor: '#ffffff',
    darkColor: '#000000'
  }
) {
  const { r, g, b } = color;
  const { brightColor, darkColor } = options;
  const brightness = Math.round((parseInt(r) * 299 + parseInt(g) * 587 + parseInt(b) * 114) / 1000);
  return brightness > 125 ? darkColor : brightColor;
}

function luminance(r, g, b) {
  var a = [r, g, b].map(function (v) {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

export function checkRatioContrast(hexColor1, hexColor2) {
  const { r: r1, g: g1, b: b1 } = hexToRgb(hexColor1);
  const { r: r2, g: g2, b: b2 } = hexToRgb(hexColor2);

  const color1luminance = luminance(r1, g1, b1);
  const color2luminance = luminance(r2, g2, b2);

  // calculate the color contrast ratio
  const ratio =
    color1luminance > color2luminance
      ? (color2luminance + 0.05) / (color1luminance + 0.05)
      : (color1luminance + 0.05) / (color2luminance + 0.05);

  const AALargeText = ratio < 1 / 3;
  const AASmallText = ratio < 1 / 4.5;
  const AAALargeText = ratio < 1 / 4.5;
  const AAASmallText = ratio < 1 / 7;

  let countFail = 0;
  [AALargeText, AASmallText, AAALargeText, AAASmallText].forEach(item => {
    countFail = countFail + (!item ? 1 : 0);
  });

  return countFail >= 2 ? false : true;
}

/**
 * Check hex string with alpha
 *
 * @param  {string} hex
 * @return {boolean}  is hex string with alpha
 */
export function isHexWithAlpha(hex) {
  return /^#([\da-f]{4}){1,2}$/i.test(hex);
}

/**
 * Check hex string
 *
 * @param  {string} hex
 * @return {boolean}  is hex string with alpha
 */
export function isHexString(hex) {
  return /^#([\da-f]{3}){1,2}$/i.test(hex);
}
